import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Stack,
} from "@mui/material";
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  Comment as CommentIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { TransactionListProps } from "../../typings";
import { FlexRow } from "../../components";

interface TransactionDetailsModalProps {
  open: boolean;
  onClose: () => void;
  transaction: TransactionListProps;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  open,
  onClose,
  transaction,
}) => {
  const theme = useTheme();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleClose = () => {
    onClose();
  };

  const totalIncoming = transaction.transaction_lines
    .filter(line => line.transaction_type === "IN")
    .reduce((sum, line) => sum + line.amount, 0);

  const totalOutgoing = transaction.transaction_lines
    .filter(line => line.transaction_type === "OUT")
    .reduce((sum, line) => sum + line.amount, 0);

  // Get shared info from the first line
  const sharedInfo = transaction.transaction_lines.length > 0 ? transaction.transaction_lines[0] : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* Header matching app theme */}
      <DialogTitle
        sx={{
          p: 3,
          bgcolor: theme.palette.primary.main,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <FlexRow align="center">
          <ReceiptIcon sx={{ mr: 1.5, fontSize: 24 }} />
          <Typography variant="h5" fontWeight="600">
            Transaction Details
          </Typography>
        </FlexRow>
        <IconButton onClick={handleClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 4 }}>
        <Box>
          {/* Transaction Info */}
          <Card sx={{ mb: 3, mt: 1, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2, color: theme.palette.text.primary }}>
                Transaction Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <FlexRow align="center">
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1, 
                      mr: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <Typography variant="body2" fontWeight="600" sx={{ color: theme.palette.primary.main }}>
                        #
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Transaction ID
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        #{transaction.id}
                      </Typography>
                    </Box>
                  </FlexRow>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <FlexRow align="center">
                    <CalendarIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Transaction Date
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        {format(new Date(transaction.date), "dd MMM yyyy")}
                      </Typography>
                    </Box>
                  </FlexRow>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <FlexRow align="center">
                    <SchoolIcon sx={{ color: theme.palette.success.main, mr: 1, fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Application ID
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        {sharedInfo?.scholarship_application_id ? `#${sharedInfo.scholarship_application_id}` : "N/A"}
                      </Typography>
                    </Box>
                  </FlexRow>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Transaction Summary */}
          <Card sx={{ mb: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1.5, color: theme.palette.text.primary }}>
                Transaction Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <FlexRow align="center" justify="center" sx={{ mb: 1 }}>
                      <TrendingUpIcon sx={{ color: theme.palette.success.main, mr: 1, fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary">
                        Total Incoming
                      </Typography>
                    </FlexRow>
                    <Typography variant="h6" fontWeight="700" sx={{ color: theme.palette.success.main }}>
                      {formatCurrency(totalIncoming)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <FlexRow align="center" justify="center" sx={{ mb: 1 }}>
                      <TrendingDownIcon sx={{ color: theme.palette.error.main, mr: 1, fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary">
                        Total Outgoing
                      </Typography>
                    </FlexRow>
                    <Typography variant="h6" fontWeight="700" sx={{ color: theme.palette.error.main }}>
                      {formatCurrency(totalOutgoing)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Comments */}
            <Card sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ p: 2.5 }}>
                <FlexRow align="center" sx={{ mb: 1.5 }}>
                  <CommentIcon sx={{ color: theme.palette.info.main, mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="600" sx={{ color: theme.palette.text.primary }}>
                    Comments
                  </Typography>
                </FlexRow>
                <Box sx={{ 
                  backgroundColor: theme.palette.grey[50], 
                  padding: 2, 
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  minHeight: "60px",
                  display: "flex",
                  alignItems: "center"
                }}>
                  <Typography variant="body2" sx={{ 
                    color: theme.palette.text.primary,
                    fontStyle: sharedInfo?.comments ? "normal" : "italic"
                  }}>
                    {sharedInfo?.comments || "No comments provided for this transaction."}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Transaction Lines */}
            <Card sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ color: theme.palette.text.primary }}>
                    Transaction Lines
                  </Typography>
                </Box>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, py: 1.5, bgcolor: theme.palette.grey[50] }}>Account</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.5, bgcolor: theme.palette.grey[50] }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.5, bgcolor: theme.palette.grey[50] }}>Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transaction.transaction_lines.map((line) => (
                        <TableRow key={line.id} hover>
                          <TableCell sx={{ py: 1.5 }}>
                            <Stack spacing={0.5}>
                              <Typography variant="body2" fontWeight="500">
                                {line.account_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {line.account}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography 
                              variant="body2" 
                              fontWeight="600"
                              sx={{ 
                                color: line.transaction_type === "IN" 
                                  ? theme.palette.success.main 
                                  : theme.palette.error.main
                              }}
                            >
                              {line.transaction_type === "IN" ? "+" : "-"}
                              {formatCurrency(line.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Chip
                              label={line.transaction_type === "IN" ? "Credit" : "Debit"}
                              color={line.transaction_type === "IN" ? "success" : "error"}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem" }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionDetailsModal;